<template>
  <table :class="$style.dolphinTable">
    <thead>
      <tr :class="$style.dolphinTable__row">
        <th
          :class="[
            $style.dolphinTable__header,
            {
              [$style['dolphinTable__header--sortable']]: column.isSortable,
            },
          ]"
          v-for="(column, columnIndex) in columnHeaders"
          :key="`Header::Column${columnIndex}`"
          @click="onClickColumnHeader(columnIndex)"
        >
          {{ column.label }}
          <IconChevron
            direction="down"
            :class="[
              $style.sortIcon,
              {
                [$style[`sortIcon--${directionString}`]]:
                  sortingColumnIndex === columnIndex,
              },
            ]"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        :class="$style.dolphinTable__row"
        v-for="(row, rowIndex) in sortedRows"
        :key="`Cell::Row${rowIndex}`"
      >
        <td
          :class="[
            $style.dolphinTable__cell,
            $style[`align--${column.align}`],
            {
              [$style['dolphinTable__cell--strong']]: !!column.strong,
            },
          ]"
          v-for="(column, columnIndex) in columnHeaders"
          :key="`Cell::Row${rowIndex}::Column${columnIndex}`"
        >
          <slot :name="column.key" :value="row[column.key]">
            {{ row[column.key] }}
          </slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import type {
  SortingDirection,
  SortResult,
  WithIndex,
} from '@/libs/utilities/sort'
import {
  SORTING_DIRECTION,
  SORTING_ORDER,
  computeNextSortingDirection,
  wrapWithIndex,
  sort,
  directionToString,
} from '@/libs/utilities/sort'

type Row = { [key: string]: unknown }
type Align = 'left' | 'right' | 'center'
export type CompareFunction<T> = (one: T, theOther: T) => number
export type Column<T> = {
  label: string
  key: string
  compareFunction?: CompareFunction<T>
  isSortable?: boolean
  align?: Align
  strong?: boolean
}

const isRightAlignedValue = (value: unknown) =>
  typeof value === 'number' || toString.call(value) === '[object Date]'
const toAlign = (value: unknown): Align =>
  isRightAlignedValue(value) ? 'right' : 'center'

type DataType = {
  sortingColumnIndex: number | null
  sortingDirection: SortingDirection
  sortingCache: Record<keyof Row, Record<SortingDirection, SortResult>>
}

export default defineComponent({
  name: 'DolphinTable',
  components: {
    IconChevron,
  },
  props: {
    columns: {
      type: Array as PropType<ReadonlyArray<Column<unknown>>>,
      required: true,
    },

    rows: {
      type: Array as PropType<ReadonlyArray<Row>>,
      required: true,
    },
  },

  data(): DataType {
    return {
      sortingColumnIndex: null,
      sortingDirection: SORTING_ORDER[0],
      sortingCache: {},
    }
  },

  computed: {
    directionString(): string {
      return directionToString(
        this.sortingDirection || SORTING_DIRECTION.NONE
      ).toLowerCase()
    },
    sortingColumn(): Maybe<Column<unknown>> {
      return this.sortingColumnIndex !== null
        ? this.columnHeaders[this.sortingColumnIndex]
        : undefined
    },
    sortedRows(): ReadonlyArray<Row> {
      if (
        this.sortingDirection === SORTING_DIRECTION.NONE ||
        this.sortingColumn === undefined
      ) {
        return this.rows
      }
      return this.sortingCache[this.sortingColumn.key][
        this.sortingDirection
      ].map(index => this.rows[index])
    },

    rowsWithIndex(): ReadonlyArray<WithIndex<Row>> {
      return wrapWithIndex(this.rows)
    },

    columnHeaders(): ReadonlyArray<Column<unknown>> {
      const firstRow = this.rows[0]
      if (firstRow === undefined) {
        return []
      }
      return this.columns.map(column => ({
        ...column,
        isSortable: column.isSortable === undefined || column.isSortable,
        align:
          column.align ||
          toAlign(firstRow[column.key as keyof typeof firstRow]),
      }))
    },
  },

  methods: {
    onClickColumnHeader(columnIndex: number): void {
      if (!this.columnHeaders[columnIndex].isSortable) {
        return
      }
      this.sortByColumnIndex(columnIndex)
    },
    sortByColumnIndex(columnIndex: number): void {
      const column = this.columnHeaders[columnIndex]
      this.sortingDirection =
        this.sortingColumnIndex === columnIndex
          ? computeNextSortingDirection(this.sortingDirection)
          : SORTING_ORDER[1]
      this.sortingColumnIndex = columnIndex

      this.createCache(column)
      this.$emit('sort', {
        key: column.key,
        direction: directionToString(this.sortingDirection),
      })
    },
    createCache(column: Column<unknown>, forceUpdate = false): void {
      if (
        this.sortingDirection === SORTING_DIRECTION.NONE ||
        (!forceUpdate &&
          this.sortingCache[column.key] !== undefined &&
          Object.prototype.hasOwnProperty.call(
            this.sortingCache[column.key],
            this.sortingDirection
          ))
      ) {
        return
      }

      const sortResult = sort(
        this.rowsWithIndex,
        column.key,
        this.sortingDirection,
        column.compareFunction
      )

      this.sortingCache = {
        ...this.sortingCache,
        [column.key]: {
          ...this.sortingCache[column.key],
          [this.sortingDirection]: sortResult,
        },
      }
    },
  },
})
</script>

<style lang="scss" module>
.dolphinTable {
  $root: &;
  $border-radius: $border-radius-v2-base;
  $border-body: 1px solid $color-border-low-emphasis;

  border-collapse: separate;
  border-spacing: 0;

  &__header {
    padding: 10px 16px 10px 40px;
    background-color: $color-surface-accent-primary;
    color: $color-text-and-object-high-emphasis-inverse;
    font-size: 12px;
    font-weight: bold;

    &--sortable {
      cursor: pointer;
      transition: color $transition-interaction;

      &:hover {
        color: $color-focus-ambiguous;
      }
    }

    &:first-child {
      border-top-left-radius: $border-radius;
    }

    &:last-child {
      border-top-right-radius: $border-radius;
    }
  }

  &__cell {
    $this: &;

    padding: 8px 24px;
    color: $color-text-and-object-high-emphasis;
    font-size: 10px;
    transition: background-color $transition-interaction;

    &--strong {
      font-weight: bold;
    }

    &:first-child {
      border-left: $border-body;
    }

    &:last-child {
      border-right: $border-body;
    }

    @at-root #{$root}__row {
      &:nth-child(n + 2) #{$this} {
        border-top: $border-body;
      }

      &:nth-child(2n + 1) #{$this} {
        background-color: $color-surface-secondary;
      }

      &:last-child #{$this} {
        border-bottom: $border-body;

        &:first-child {
          border-bottom-left-radius: $border-radius;
        }

        &:last-child {
          border-bottom-left-radius: $border-radius;
        }
      }

      &:hover #{$this} {
        background-color: $color-surface-quaternary;
      }
    }
  }
}

.align {
  &--right {
    text-align: right;
  }

  &--center {
    text-align: center;
  }

  &--left {
    text-align: left;
  }
}

.sortIcon {
  font-size: 24px;
  opacity: 0;
  transition:
    opacity $transition-interaction,
    transform $transition-interaction;

  &--asc {
    opacity: 1;
    transform: rotate(180deg);
  }

  &--desc {
    opacity: 1;
    transform: rotate(0deg);
  }
}
</style>
